import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { KnowledgeCheckContent } from "../../../../../types/newtypes";
import { useScoreTracking } from "../../../../../context/ScoreTrackingContext";

interface UseKnowledgeCheckPreviewProps {
  content: KnowledgeCheckContent;
  onCardStateChange?: (isCompleted: boolean, canProceed: boolean) => void;
  cardId: string;
}

export const isOptionCorrect = (option: any): boolean => {
  if (typeof option?.__isCorrect === 'boolean') return option.__isCorrect;
  const v = option?.is_correct ?? option?.isCorrect ?? option?.correct;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') return v.toLowerCase() === 'true' || v === '1';
  if (typeof v === 'number') return v === 1;
  return false;
};

// Simple string hash to seed the shuffle — deterministic for a given string
export const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};

// Per-session seed — generated once per browser session, so shuffle order
// varies between sessions but stays stable within one.
const SESSION_SEED_KEY = "pedagogue_shuffle_seed";

export const getSessionSeed = (): number => {
  if (typeof sessionStorage === "undefined") {
    return 0;
  }
  const existing = sessionStorage.getItem(SESSION_SEED_KEY);
  if (existing !== null) {
    return parseInt(existing, 10);
  }
  const seed = Math.floor(Math.random() * 2147483647);
  sessionStorage.setItem(SESSION_SEED_KEY, String(seed));
  return seed;
};

// Seeded pseudo-random number generator (mulberry32)
const seededRandom = (seed: number): () => number => {
  let s = seed;
  return () => {
    s |= 0;
    s = (s + 0x6D2B79F5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

// Fisher-Yates shuffle with seeded PRNG for deterministic but randomised order
export const shuffleIndices = (length: number, seed: number): number[] => {
  const indices = Array.from({ length }, (_, i) => i);
  if (length <= 1) {
    return indices;
  }
  const random = seededRandom(seed);
  for (let i = indices.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }
  return indices;
};

export const useKnowledgeCheckPreview = ({
  content,
  onCardStateChange,
  cardId,
}: UseKnowledgeCheckPreviewProps) => {
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [isSelectionCorrect, setIsSelectionCorrect] = useState<boolean | null>(
    null,
  );
  const [feedbackTitle, setFeedbackTitle] = useState("");
  const [feedbackMessage, setFeedbackMessage] = useState("");

  const { incrementMistakes, updateCardScore, isCardCompleted, getMistakes } =
    useScoreTracking();

  const maxScore = content?.max_score ?? 0;
  const previousCardIdRef = useRef<string | null>(null);

  // Shuffle option indices once per cardId+session, stable across re-renders and resets.
  // Combining sessionSeed with cardId ensures order varies between sessions (preventing
  // cross-user memorization) but stays stable within the current session.
  const sessionSeed = useMemo(() => getSessionSeed(), []);
  const shuffledIndices = useMemo(() => {
    const optionsLength = content?.options?.length ?? 0;
    if (optionsLength === 0) {
      return [];
    }
    const seed = hashString(`${sessionSeed}-${cardId || "default"}`);
    return shuffleIndices(optionsLength, seed);
  }, [content?.options?.length, cardId, sessionSeed]);

  // Derive shuffled options from the stable index mapping
  const shuffledOptions = useMemo(() => {
    if (!content?.options) {
      return [];
    }
    return shuffledIndices.map((originalIndex) => content.options[originalIndex]);
  }, [content?.options, shuffledIndices]);

  useEffect(() => {
    if (
      previousCardIdRef.current !== null &&
      previousCardIdRef.current !== cardId
    ) {
      setSelectedAnswer(null);
      setIsSelectionCorrect(null);
      setFeedbackTitle("");
      setFeedbackMessage("");
    }
    previousCardIdRef.current = cardId;
  }, [cardId]);

  // Disable Next button until correct answer is selected
  useEffect(() => {
    if (isSelectionCorrect === true) {
      onCardStateChange?.(true, true);
    } else {
      onCardStateChange?.(false, false);
    }
  }, [isSelectionCorrect, onCardStateChange]);

  const evaluateSelection = useCallback(
    (shuffledIndex: number) => {
      // Map from shuffled display index back to original option
      const originalIndex = shuffledIndices[shuffledIndex];
      const option = content?.options?.[originalIndex];
      if (!option) {
        return;
      }

      const isCorrect = isOptionCorrect(option);
      setSelectedAnswer(shuffledIndex);
      setIsSelectionCorrect(isCorrect);

      const remediationText =
        (option?.remediation?.type === "text" &&
          option?.remediation?.content) ||
        "";

      if (isCorrect) {
        setFeedbackTitle("Correct!");
        setFeedbackMessage(remediationText);

        if (cardId && !isCardCompleted(cardId)) {
          const mistakes = getMistakes(cardId) || 0;
          updateCardScore(cardId, mistakes, maxScore);
        }

        onCardStateChange?.(true, true);
      } else {
        setFeedbackTitle("Not quite right");
        setFeedbackMessage(remediationText);

        if (cardId && !isCardCompleted(cardId)) {
          incrementMistakes(cardId, maxScore);
        }

        onCardStateChange?.(false, false);
      }
    },
    [
      shuffledIndices,
      content?.options,
      cardId,
      getMistakes,
      incrementMistakes,
      isCardCompleted,
      maxScore,
      onCardStateChange,
      updateCardScore,
    ],
  );

  const handleOptionSelect = useCallback(
    (index: number) => {
      if (isSelectionCorrect === true) return;
      evaluateSelection(index);
    },
    [evaluateSelection, isSelectionCorrect],
  );

  const handleResetAnswers = useCallback(() => {
    if (isSelectionCorrect === true) return;
    setSelectedAnswer(null);
    setIsSelectionCorrect(null);
    setFeedbackTitle("");
    setFeedbackMessage("");
  }, [isSelectionCorrect]);

  return {
    selectedAnswer,
    isSelectionCorrect,
    feedbackTitle,
    feedbackMessage,
    shuffledOptions,
    handleOptionSelect,
    handleResetAnswers,
  };
};
